import dedent from 'ts-dedent'
import {
  ColumnConditions,
  ConditionType,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentValue
} from '../types'
import { formatValue, getValueTypeFromSchema, isExpressionValue } from '../utils'
import { SelectWorkflowComponent } from './common/selectcomponent'

class CaseWhen extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.casewhen',
        group: 'Data Preparation',
        title: 'Case When',
        description: `
          This component generates column values that depend on a set of specified conditions.
        `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'conditions',
          type: WorkflowComponentParamType.Condition,
          parent: 'source',
          title: 'Conditions and returns',
          placeholder: 'workflows.parameterForm.selectAColumn'
        },
        {
          name: 'else',
          type: WorkflowComponentParamType.String,
          title: 'Else',
          placeholder: 'workflows.parameterForm.returnValue',
          optional: true,
          helper: 'workflows.parameterForm.elseHelper',
          allowExpressions: true
        },
        {
          name: 'newcolumn',
          type: WorkflowComponentParamType.String,
          title: 'Name for new column',
          placeholder: 'workflows.parameterForm.returnColumn',
          trimSpaces: true,
          default: 'new_column',
          allowExpressions: false
        }
      ]
    )
  }

  getConditions(conditionsStr: string) {
    const conditions: ColumnConditions[] = JSON.parse(conditionsStr)
    return conditions
      ?.map((columnCondition) => {
        const validConditions = columnCondition?.conditions?.filter(
          (condition) =>
            condition.condition === ConditionType.Null ||
            condition.condition === ConditionType.True ||
            condition.condition === ConditionType.False ||
            condition.value !== ''
        )
        if (validConditions?.length) {
          return {
            ...columnCondition,
            conditions: validConditions
          }
        }
      })
      .filter((column) => column) as ColumnConditions[]
  }

  getInnerSql({ workflow, componentName, params, connection, schemas }: WorkflowComponentCodeParams) {
    const conditions = this.getConditions(params.conditions as string)
    const whenThen: Array<string> = []
    for (let index = 0; index < conditions.length; index += 1) {
      const { column, conditions: columnConditions } = conditions[index]
      let formattedValue
      let formattedReturnValue
      if (columnConditions?.length) {
        for (let x = 0; x < columnConditions.length; x += 1) {
          const { condition, value, return: returnValue } = columnConditions[x]
          if (isExpressionValue(returnValue)) {
            formattedReturnValue = returnValue.replace(/\{\{/g, '(').replace(/\}\}/g, ')')
          } else {
            formattedReturnValue = formatValue(returnValue, connection.type, undefined)
          }
          if (isExpressionValue(value)) {
            formattedValue = value.replace(/\{\{/g, '(').replace(/\}\}/g, ')')
            formattedValue = condition === ConditionType.Contains ? `'%' || ${formattedValue} || '%'` : formattedValue
          } else {
            const valueType = getValueTypeFromSchema(column, schemas?.input.source as TableSchema | undefined)
            const validValue = condition === ConditionType.Contains ? `%${value}%` : value
            formattedValue = formatValue(validValue, connection.type, valueType)
          }
          const isSpecialValue =
            condition === ConditionType.Null || condition === ConditionType.True || condition === ConditionType.False
          const when = isSpecialValue
            ? `WHEN ${column} ${condition}`
            : condition === ConditionType.Contains
            ? `WHEN ${column} LIKE ${formattedValue}`
            : `WHEN ${column} ${condition} ${formattedValue}`
          whenThen.push(`${when} THEN ${formattedReturnValue}`)
        }
      }
    }
    let formattedElse
    const elseValue = (workflow?.componentValues?.[componentName]?.else as WorkflowComponentValue)?.value as string
    if (isExpressionValue(elseValue)) {
      formattedElse = elseValue.replace(/\{\{/g, '(').replace(/\}\}/g, ')')
    } else {
      formattedElse = formatValue(params.else as string, connection.type, undefined)
    }
    let query = ''
    if (connection.type === Provider.DatabricksRest) {
      query = dedent`SELECT /*+ REPARTITION(40) */
                          *,
                          CASE
                            ${whenThen.join('\n')}
                          ELSE ${formattedElse}
                          END AS ${params.newcolumn}
                        FROM ${connection.quoteName(params.source as string)}`
    } else {
      query = dedent`SELECT
                          *,
                          CASE
                            ${whenThen.join('\n')}
                          ELSE ${formattedElse}
                          END AS ${params.newcolumn}
                        FROM ${connection.quoteName(params.source as string)}`
    }

    return query
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { CaseWhen }
